<template>
    <div class="flex flex-col items-center justify-center h-full">
        <div class="container mx-auto px-4 py-8">
            <div class="max-w-xl mx-auto">
                <div class="bg-echoes-background-black rounded-lg shadow-lg border border-echoes-background-gray">
                    <div class="p-8 text-center">
                        <!-- Loading State -->
                        <div v-if="connectStatus === 'awaiting'" class="space-y-6">
                            <div class="inline-flex items-center justify-center w-16 h-16 bg-yellow-600 rounded-full">
                                <ButtonLoading size="md" :showText="false" />
                            </div>
                            <div>
                                <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                    Finalizing your Stripe connection...
                                </h1>
                                <p class="text-gray-600 dark:text-gray-300">
                                    This usually takes a few seconds. Keep this window open while we finish connecting your Stripe account. 
                                </p>
                            </div>
                        </div>

                        <!-- Success State -->
                        <div v-else-if="connectStatus === 'success'" class="space-y-6">
                            <div class="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full">
                                <svg class="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                                </svg>
                            </div>
                            <div>
                                <h1 class="text-2xl font-bold text-green-900 dark:text-green-100 mb-2">
                                    Stripe connected successfully! 
                                </h1>
                                <p class="text-gray-600 dark:text-gray-300 mb-6">
                                    Your Stripe account is now linked to Portex. You can receive payouts for your datasets and evals.
                                </p>
                            </div>
                            
                            <!-- Success Actions -->
                            <div class="space-y-8">
                                <div class="bg-green-400/10 border border-green-200 dark:border-green-800 rounded-lg p-4">
                                    <div class="flex items-start space-x-3">
                                        <svg class="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg>
                                        <div class="text-left">
                                            <h3 class="font-medium text-green-900 dark:text-green-100">Next steps:</h3>
                                            <ul class="text-sm mt-1 space-y-1">
                                                <li>• Set prices for your datasets and evals</li>
                                                <li>• Publish your first listings on the Datalab</li>
                                                <li>• Track revenue in Data Studio</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                
                                <div class="flex flex-col gap-3 justify-center">
                                    <div class="flex gap-3 justify-center">
                                        <button @click="goToCreateDataset" class="success-button">
                                            <div class="flex items-center justify-center space-x-2">
                                                <svg class="w-4 h-4 transition-transform group-hover:scale-110 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                                                </svg>
                                                <span>Create a dataset or eval</span>
                                            </div>
                                        </button>
                                        
                                        <button 
                                            @click="goToDataStudio"
                                            class="px-6 py-2 border border-echoes-background-gray rounded-lg text-white font-medium hover:bg-echoes-primary/20 transition-colors"
                                        >
                                            Open Data Studio
                                        </button>
                                    </div>
                                    <div>
                                        <div @click="goToExplore" class="text-echoes-primary cursor-pointer hover:underline">Explore Datalab</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Error State -->
                        <div v-else-if="connectStatus === 'error'" class="space-y-6">
                            <div class="inline-flex items-center justify-center w-16 h-16 bg-red-900 rounded-full">
                                <svg class="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </div>
                            <div>
                                <h1 class="text-2xl font-bold text-red-900 dark:text-red-100 mb-2">
                                    We couldn’t connect your Stripe account
                                </h1>
                                <p class="text-gray-600 dark:text-gray-300 mb-6">
                                    Something interrupted the connection to Stripe. Your Portex account is unchanged.
                                </p>
                            </div>

                            <!-- Error Actions -->
                            <div class="space-y-8">
                                <div class="bg-echoes-background-gray rounded-lg p-4">
                                    <div class="flex items-start space-x-3">
                                        <svg class="w-6 h-6 text-red-800 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                                        </svg>
                                        <div class="text-left">
                                            <h3 class="font-medium text-red-900 dark:text-red-100">Possible solutions:</h3>
                                            <ul class="text-sm mt-1 space-y-1">
                                                <li>• Check your internet connection</li>
                                                <li>• Try the Stripe flow again in a few minutes</li>
                                                <li>• Consider being paid in USDC (requires a wallet)</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                
                                <div class="flex flex-col gap-3 justify-center">
                                    <div class="flex gap-3 justify-center">
                                        <button @click="retryConnection" class="success-button">
                                            <div class="flex items-center justify-center space-x-2">
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                                                </svg>
                                                <span>Try connecting again</span>
                                            </div>
                                        </button>
                                        
                                        <button 
                                            @click="goToDataStudio"
                                            class="px-6 py-2 border border-echoes-background-gray rounded-lg text-white font-medium hover:bg-echoes-primary/20 transition-colors"
                                        >
                                            <div class="flex items-center justify-center space-x-2">
                                                <span>Continue without Stripe</span>
                                            </div>
                                        </button>
                                    </div>
                                    <div>
                                        <div @click="goToCreateDataset" class="text-echoes-primary cursor-pointer hover:underline flex items-center justify-center group">
                                            <svg class="w-4 h-4 transition-transform group-hover:scale-110 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                                            </svg>
                                            <div>
                                                Create a dataset or eval
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {ref, onMounted} from 'vue'
import { useRouter } from 'vue-router'
const connectStatus = ref('awaiting') // success, error, awaiting
import axios from '@/plugins/axios'
import { useLayoutComponents } from '@/composables/layout.js'
import { useStorage } from '@/composables/use-storage.js'

const { ButtonLoading } = useLayoutComponents()
const router = useRouter()
const { saveUserInfo, getUserInfo } = useStorage()

const finalizeStripeConnect = async () => {
    const endpoint = '/api/stripe/finalize-connect'
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const userId = params.get('state')
    const response = await axios.post(endpoint, {
        code: code,
        userId: userId,
    })
    if (response.data.status === 201) {
        let userOldInfos = getUserInfo()

        userOldInfos.seller.stripe_account_id = response.data.stripe_account_id

        await saveUserInfo(userOldInfos)

        connectStatus.value = 'success'
    } else {
        connectStatus.value = 'error'
    }
}

const goToCreateDataset = () => {
    router.push('/dataset-create')
}

const goToExplore = () => {
    router.push('/explore')
}

const goToDataStudio = () => {
    router.push('/creator-profile')
}

const retryConnection = () => {
    router.push('/stripe/connect')
}

onMounted(() => {
    finalizeStripeConnect()
})
</script>

<style scoped lang="postcss">
.success-button {
    @apply bg-echoes-primary hover:bg-echoes-primary/80 px-6 py-2 text-white rounded-lg font-medium transition-all duration-200;
}

.error-retry-button {
    @apply bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800;
}
</style>